import { SupplyOrderState } from 'data/__generated__';

import { dateFromString } from 'helpers/dateTimes';

import { SUPPLY_ORDER_PLURAL_STATES_LABELS } from './states';
import type { SupplyOrderRow } from './supplyOrderViews';

export type SupplyOrderKanbanColumn = { title: string; supplyOrders: SupplyOrderRow[] };

type SortableField = 'orderDate' | 'dateDeliveryScheduled' | 'dateDelivery';

/** Le discriminant « manuel » de la colonne « Créés manuels », repris tel quel : `nextOrderDate` posée. */
function isManualSupplyOrder(supplyOrder: SupplyOrderRow): boolean {
    return Boolean(supplyOrder.nextOrderDate);
}

function sortByField(field: SortableField) {
    return (supplyOrderA: SupplyOrderRow, supplyOrderB: SupplyOrderRow) => {
        if (!supplyOrderA[field]) {
            return -1;
        }
        if (!supplyOrderB[field]) {
            return 1;
        }
        return dateFromString(supplyOrderA[field] as string) > dateFromString(supplyOrderB[field] as string) ? 1 : -1;
    };
}

function sortByPrice(supplyOrderA: SupplyOrderRow, supplyOrderB: SupplyOrderRow) {
    return (supplyOrderA.priceHT ?? 0) - (supplyOrderB.priceHT ?? 0);
}

/** Date de livraison prévue décroissante, celles qui n'en ont pas à la fin. */
function bySoonestScheduledDelivery(supplyOrderA: SupplyOrderRow, supplyOrderB: SupplyOrderRow) {
    if (!supplyOrderA.dateDeliveryScheduled) {
        return 1;
    }
    if (!supplyOrderB.dateDeliveryScheduled) {
        return -1;
    }
    return dateFromString(supplyOrderA.dateDeliveryScheduled) > dateFromString(supplyOrderB.dateDeliveryScheduled)
        ? -1
        : 1;
}

/**
 * Les colonnes du kanban. Neuf états, mais dix colonnes : `Created` est scindé entre les achats
 * générés par le cron et ceux créés à la main, qui ne se traitent pas de la même façon.
 *
 * Les tris s'ENCHAÎNENT volontairement (`.sort(a).sort(b)`) : `Array.prototype.sort` est stable, donc
 * le dernier appelé est la clé principale et le précédent départage. C'est le comportement d'origine,
 * reproduit tel quel.
 */
export function groupSupplyOrdersIntoColumns(supplyOrders: SupplyOrderRow[]): SupplyOrderKanbanColumn[] {
    const byState = (state: SupplyOrderState, isManual?: boolean) =>
        supplyOrders
            .filter(
                (supplyOrder) =>
                    supplyOrder.state === state &&
                    (isManual === undefined || isManualSupplyOrder(supplyOrder) === isManual),
            )
            .sort(bySoonestScheduledDelivery);

    return [
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Created],
            supplyOrders: byState(SupplyOrderState.Created, false).sort(sortByPrice).sort(sortByField('orderDate')),
        },
        {
            title: 'Créés manuels',
            supplyOrders: byState(SupplyOrderState.Created, true).sort(sortByPrice).sort(sortByField('orderDate')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Ready],
            supplyOrders: byState(SupplyOrderState.Ready).sort(sortByField('orderDate')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Ordered],
            supplyOrders: byState(SupplyOrderState.Ordered).sort(sortByField('orderDate')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Confirmed],
            supplyOrders: byState(SupplyOrderState.Confirmed).sort(sortByField('dateDeliveryScheduled')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.OnHold],
            supplyOrders: byState(SupplyOrderState.OnHold).sort(sortByField('dateDeliveryScheduled')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Received],
            supplyOrders: byState(SupplyOrderState.Received).sort(sortByField('dateDelivery')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Checked],
            supplyOrders: byState(SupplyOrderState.Checked).sort(sortByField('dateDelivery')),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.InRack],
            supplyOrders: byState(SupplyOrderState.InRack).sort(sortByField('dateDelivery')).reverse(),
        },
        {
            title: SUPPLY_ORDER_PLURAL_STATES_LABELS[SupplyOrderState.Aborted],
            supplyOrders: byState(SupplyOrderState.Aborted).sort(sortByField('dateDeliveryScheduled')),
        },
    ];
}
